import { faClipboard } from "@fortawesome/free-solid-svg-icons/faClipboard";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { faPaperclip } from "@fortawesome/free-solid-svg-icons/faPaperclip";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons/faPaperPlane";
import { KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Clickable } from "../../../components/Clickable";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { orgChatSliceSelectors } from "../../../redux/chat/orgChat.selector";
import { orgChatActions } from "../../../redux/chat/orgChat.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";

const ComposerBar = styled(Flex)`
  position: sticky;
  bottom: 0;
  width: 100%;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  background-color: ${({ theme }) => theme.white};
  border-top: 1px solid ${({ theme }) => theme.sub};

  .atira-input-wrapper input {
    border-radius: ${Rounded.pill};
  }
`;

const ComposerButton = styled(Clickable)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.lightSub};
  color: ${({ theme }) => theme.main};
  transition: background-color 0.15s ease;

  &:hover:not([aria-disabled="true"]) {
    background-color: ${({ theme }) => theme.sub};
  }
`;

const ComposerSendButton = styled(ComposerButton)`
  background-color: ${({ theme }) => theme.main};

  &:hover:not([aria-disabled="true"]) {
    background-color: ${({ theme }) => theme.darkMain};
  }
`;

export const OrgChatComposer: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [text, setText] = useState("");

  const sending = useAppSelector(orgChatSliceSelectors.selectSending);
  const canSend = !sending && Boolean(text.trim());

  const handleSend = async () => {
    const trimmed = text.trim();

    if (!trimmed || sending) {
      return;
    }

    setText("");

    try {
      await dispatch(orgChatActions.sendMessage({ text: trimmed })).unwrap();
    } catch (e) {
      setText((current) => current || trimmed);
      AtiraToast.apiError(e);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSend();
    }
  };

  return (
    <ComposerBar>
      <Input
        dir="auto"
        placeholder={t("teamChat.inputPlaceholder")}
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, Lengths.VERY_LONG))}
        onKeyDown={handleKeyDown}
        dataTestId={TestIDs.orgChat.input}
      />

      <ComposerSendButton
        onClick={handleSend}
        disabled={!canSend}
        aria-disabled={!canSend}
        title={t("teamChat.send")}
        dataTestId={TestIDs.orgChat.sendButton}
      >
        <AtiraIcon icon={faPaperPlane} size="lg" color="white" />
      </ComposerSendButton>

      <ComposerButton
        disabled
        aria-disabled
        title={t("common.coming_soon")}
        dataTestId={TestIDs.orgChat.attach}
      >
        <AtiraIcon icon={faPaperclip} size="lg" color="main" />
      </ComposerButton>

      <ComposerButton
        disabled
        aria-disabled
        title={t("common.coming_soon")}
        dataTestId={TestIDs.orgChat.record}
      >
        <AtiraIcon icon={faMicrophone} size="lg" color="main" />
      </ComposerButton>

      <ComposerButton
        disabled
        aria-disabled
        title={t("common.coming_soon")}
        dataTestId={TestIDs.orgChat.templates}
      >
        <AtiraIcon icon={faClipboard} size="lg" color="main" />
      </ComposerButton>
    </ComposerBar>
  );
};
